import { createId } from "@paralleldrive/cuid2";
import { PgnParser } from "../pgn/PgnParser";
export var TopicDifficulty;
(function (TopicDifficulty) {
    TopicDifficulty["Beginner"] = "Beginner";
    TopicDifficulty["Rookie"] = "Rookie";
    TopicDifficulty["Intermediate"] = "Intermediate";
    TopicDifficulty["Advanced"] = "Advanced";
})(TopicDifficulty || (TopicDifficulty = {}));
export var ContentStatus;
(function (ContentStatus) {
    ContentStatus["Draft"] = "Draft";
    ContentStatus["Published"] = "Published";
    ContentStatus["Archived"] = "Archived";
})(ContentStatus || (ContentStatus = {}));
export class Topic {
    id;
    title;
    description;
    difficultyLevel;
    status;
    isPublic;
    ownerId;
    owner;
    tags;
    urls;
    workbookRef;
    originalTopicId;
    constructor(data) {
        this.id = createId();
        this.title = data.title;
        this.description = data.description;
        this.difficultyLevel = data.difficultyLevel;
        this.status = data.status || ContentStatus.Draft;
        this.isPublic = data.isPublic;
        this.ownerId = data.ownerId;
        this.owner = data.owner;
        this.tags = data.tags || [];
        this.urls = data.urls || [];
        this.workbookRef = data.workbookRef;
        this.originalTopicId = data.originalTopicId;
    }
    update(data) {
        this.title = data.title || this.title;
        this.description = data.description || this.description;
        this.difficultyLevel = data.difficultyLevel || this.difficultyLevel;
        this.status = data.status || this.status;
        if (data.tags)
            this.tags = data.tags;
        if (data.urls)
            this.urls = data.urls;
        if (data.workbookRef !== undefined)
            this.workbookRef = data.workbookRef;
        // isPublic and ownerId can't be changed
    }
}
export class Scenario {
    static ORDER_INTERVAL = 10;
    static NORMALIZE_THRESHOLD = 2;
    id;
    topicId;
    title;
    isPuzzle;
    isHomework;
    pgn;
    status;
    order;
    puzzleLevel;
    createdAt;
    updatedAt;
    constructor(topicId, data) {
        const normalizedPgn = PgnParser.normalize(data.pgn);
        const headers = PgnParser.extractHeaders(normalizedPgn);
        this.id = createId();
        this.topicId = topicId;
        this.title = headers.get("Event") || "Untitled Example";
        this.isPuzzle = normalizedPgn.includes(PgnParser.REGEX.PUZZLE_MARKER);
        this.isHomework = headers.get("Homework") === "1";
        this.pgn = normalizedPgn;
        this.status = data.status || ContentStatus.Published;
        this.order = data.order ?? 0;
        this.puzzleLevel = PgnParser.parsePuzzleLevel(headers.get("PuzzleLevel"));
        this.createdAt = data.createdAt || new Date();
        this.updatedAt = data.updatedAt || new Date();
    }
    update(data) {
        const normalizedPgn = PgnParser.normalize(data.pgn);
        const headers = PgnParser.extractHeaders(normalizedPgn);
        this.title = headers.get("Event") || this.title;
        this.isPuzzle = normalizedPgn.includes(PgnParser.REGEX.PUZZLE_MARKER);
        this.isHomework = headers.get("Homework") === "1";
        this.pgn = normalizedPgn;
        this.puzzleLevel = PgnParser.parsePuzzleLevel(headers.get("PuzzleLevel"));
        if (data.order !== undefined) {
            this.order = data.order;
        }
        if (data.status !== undefined) {
            this.status = data.status;
        }
        this.updatedAt = new Date();
    }
    static getNextOrder(scenarios) {
        if (!scenarios.length)
            return Scenario.ORDER_INTERVAL;
        return Math.max(...scenarios.map((s) => s.order)) + Scenario.ORDER_INTERVAL;
    }
    static getOrderBetween(beforeOrder, afterOrder) {
        return Math.floor((beforeOrder + afterOrder) / 2);
    }
    static shouldNormalize(scenarios) {
        if (scenarios.length < 2)
            return false;
        const orderedScenarios = [...scenarios].sort((a, b) => a.order - b.order);
        for (let i = 1; i < orderedScenarios.length; i++) {
            const gap = orderedScenarios[i].order - orderedScenarios[i - 1].order;
            if (gap < Scenario.NORMALIZE_THRESHOLD)
                return true;
        }
        return false;
    }
    static normalizeOrders(scenarios) {
        const orderedScenarios = [...scenarios].sort((a, b) => a.order - b.order);
        return orderedScenarios.map((scenario, index) => {
            const normalizedScenario = new Scenario(scenario.topicId, {
                pgn: scenario.pgn,
                order: (index + 1) * Scenario.ORDER_INTERVAL,
                status: scenario.status,
            });
            normalizedScenario.id = scenario.id; // Preserve the original ID
            return normalizedScenario;
        });
    }
    static reorder(scenarios, fromIndex, toIndex) {
        const orderedScenarios = [...scenarios].sort((a, b) => a.order - b.order);
        const [movedScenario] = orderedScenarios.splice(fromIndex, 1);
        orderedScenarios.splice(toIndex, 0, movedScenario);
        // Calculate new order for the moved item
        const newOrder = (() => {
            if (toIndex === 0) {
                // Moving to start - use half of first remaining item's order
                return orderedScenarios[1].order / 2;
            }
            else if (toIndex === orderedScenarios.length) {
                // Moving to end - add interval to last item's order
                return orderedScenarios[toIndex - 1].order + Scenario.ORDER_INTERVAL;
            }
            else {
                // Moving between items - use middle value
                return Scenario.getOrderBetween(orderedScenarios[toIndex - 1].order, orderedScenarios[toIndex + 1].order);
            }
        })();
        movedScenario.order = newOrder;
        // Check if we need to normalize
        if (Scenario.shouldNormalize(orderedScenarios)) {
            return Scenario.normalizeOrders(orderedScenarios);
        }
        return orderedScenarios;
    }
}
